import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, CreditCard, ReceiptText, FileText, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Section2Import({ onImported, vendedores = [], periodo }) {
  const [loadingL, setLoadingL] = useState(false);
  const [loadingC, setLoadingC] = useState(false);
  const [progress, setProgress] = useState(null); // {page,total}
  const [vendedor, setVendedor] = useState("");
  const lExcelRef = useRef(null);
  const lPdfRef = useRef(null);
  const cExcelRef = useRef(null);
  const cPdfRef = useRef(null);

  const notifyResult = (data) => {
    toast.success(data.message);
    (data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
  };

  const pollJob = (jobId) =>
    new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const { data } = await api.get(`/import-status/${jobId}`);
          if (data.progress) setProgress(data.progress);
          if (data.status === "done") return resolve(data);
          if (data.status === "error") return reject(new Error(data.error || "Error en el procesamiento"));
          setTimeout(tick, 1000);
        } catch (e) {
          reject(e);
        }
      };
      tick();
    });

  const uploadLineas = async (file) => {
    if (!file) return;
    setLoadingL(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("periodo", periodo || "");
      const { data } = await api.post("/import/lineas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      notifyResult(data);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar líneas");
    } finally {
      setLoadingL(false);
      if (lExcelRef.current) lExcelRef.current.value = "";
      if (lPdfRef.current) lPdfRef.current.value = "";
    }
  };

  const uploadCuentas = async (file) => {
    if (!file) return;
    if (!vendedor.trim()) { toast.error("Indique el vendedor para las cuentas"); if (cExcelRef.current) cExcelRef.current.value = ""; if (cPdfRef.current) cPdfRef.current.value = ""; return; }
    setLoadingC(true);
    setProgress(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vendedor", vendedor.trim());
      fd.append("periodo", periodo || "");
      const { data } = await api.post("/import/cuentas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.background && data.job_id) {
        toast.info("Procesando PDF en segundo plano…");
        const done = await pollJob(data.job_id);
        toast.success(`Cuentas cargadas: ${done.count} facturas · ${done.clientes} clientes · ${done.method || "texto"}`);
        (done.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      } else {
        notifyResult(data);
      }
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error al cargar cuentas");
    } finally {
      setLoadingC(false);
      setProgress(null);
      if (cExcelRef.current) cExcelRef.current.value = "";
      if (cPdfRef.current) cPdfRef.current.value = "";
    }
  };

  return (
    <div data-testid="section2-import" className="rounded-xl border border-emerald-100 bg-white p-4 space-y-3">
      <div data-testid="section2-periodo" className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-1.5">
        Las cargas se guardan para el mes <strong>{periodo || "—"}</strong> (histórico por mes). Excel/CSV es la vía más rápida; el PDF con texto también se procesa en segundos.
      </div>
      {loadingC && progress && progress.total > 0 && (
        <div data-testid="cuentas-progress" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="flex items-center justify-between text-xs text-emerald-800 mb-1">
            <span>Procesando cuentas…</span>
            <span>página {progress.page} de {progress.total}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-emerald-100 overflow-hidden">
            <div className="h-full bg-emerald-600 transition-all" style={{ width: `${Math.round((progress.page / progress.total) * 100)}%` }} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-100 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <CreditCard className="h-4 w-4 text-emerald-700" />
            <span className="text-sm font-semibold text-slate-800">2.1 · Listado de Líneas de Crédito</span>
          </div>
          <p className="text-[11px] text-slate-500 mb-2">Líneas ACTIVAS: fechas, línea, adicionales, deuda, saldo y disponible neto.</p>
          <input ref={lExcelRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="lineas-excel-input" onChange={(e) => uploadLineas(e.target.files?.[0])} />
          <input ref={lPdfRef} type="file" accept=".pdf" className="hidden" data-testid="lineas-pdf-input" onChange={(e) => uploadLineas(e.target.files?.[0])} />
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="lineas-excel-btn" onClick={() => lExcelRef.current?.click()} disabled={loadingL} className="w-full bg-emerald-700 hover:bg-emerald-800">
              {loadingL ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}Excel/CSV (rápido)
            </Button>
            <Button data-testid="lineas-pdf-btn" onClick={() => lPdfRef.current?.click()} disabled={loadingL} variant="outline" className="w-full border-emerald-200 text-emerald-800 hover:bg-emerald-50">
              {loadingL ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}PDF
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-100 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <ReceiptText className="h-4 w-4 text-emerald-700" />
            <span className="text-sm font-semibold text-slate-800">2.2 · Listado de Cuentas a Recibir</span>
          </div>
          <p className="text-[11px] text-slate-500 mb-2">Factura por factura (Venc.Orig. + Sal.Correg). Vencido = venc. original anterior a hoy.</p>
          <input list="s2-vendedores" data-testid="cuentas-vendedor-input" value={vendedor} onChange={(e) => setVendedor(e.target.value)} placeholder="Vendedor asignado"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <datalist id="s2-vendedores">{vendedores.map((v) => <option key={v} value={v} />)}</datalist>
          <input ref={cExcelRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="cuentas-excel-input" onChange={(e) => uploadCuentas(e.target.files?.[0])} />
          <input ref={cPdfRef} type="file" accept=".pdf" className="hidden" data-testid="cuentas-pdf-input" onChange={(e) => uploadCuentas(e.target.files?.[0])} />
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="cuentas-excel-btn" onClick={() => cExcelRef.current?.click()} disabled={loadingC} className="w-full bg-emerald-700 hover:bg-emerald-800">
              {loadingC ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}Excel/CSV (rápido)
            </Button>
            <Button data-testid="cuentas-pdf-btn" onClick={() => cPdfRef.current?.click()} disabled={loadingC} variant="outline" className="w-full border-emerald-200 text-emerald-800 hover:bg-emerald-50">
              {loadingC ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
